// [T155P3] Categorization Health sub-signal
// Shows % of transactions with a category, with a progress bar.
// Source: Redux state.transactions.reviewQueue (populated by fetchReviewQueue thunk).
import { useNavigate } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { Users } from 'lucide-react';
import type { ReviewQueue } from '@/lib/types';

type StatusLevel = 'green' | 'amber' | 'red';

const statusTextClass: Record<StatusLevel, string> = {
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
};

interface Props {
  reviewQueue: ReviewQueue | null;
  status: StatusLevel;
}

export default function CategorizationHealth({ reviewQueue, status }: Props) {
  const navigate = useNavigate();

  if (!reviewQueue) {
    return (
      <div data-testid="categorization-health" className="space-y-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Categorization</span>
        </div>
        <div className="h-2 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (reviewQueue.total === 0) {
    return (
      <div data-testid="categorization-health" className="space-y-1">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Categorization</span>
        </div>
        <p className="text-sm text-green-600 dark:text-green-400">No transactions yet</p>
      </div>
    );
  }

  const categorized = reviewQueue.total - reviewQueue.uncategorized.count;
  const pct = Math.round((categorized / reviewQueue.total) * 100);

  return (
    <div data-testid="categorization-health" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Categorization</span>
        </div>
        <span className={`text-sm font-semibold tabular-nums ${statusTextClass[status]}`}>
          {pct}%
        </span>
      </div>
      <Progress value={pct} className="h-2" />
      {reviewQueue.uncategorized.count > 0 && (
        <button
          onClick={() => navigate('/review?tab=uncategorized')}
          className="min-h-[44px] flex items-center text-xs text-primary underline-offset-2 hover:underline"
        >
          Review {reviewQueue.uncategorized.count} uncategorized →
        </button>
      )}
    </div>
  );
}
